import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, ChevronUp, ChevronDown, AlertTriangle, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import { pref } from '../utils/moneda';
import Modal from '../components/Modal';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';

// ============================================================
// Dashboard de Ventas (#95 + #96), siempre de la empresa activa. Un solo
// tablero para fábricas (facturas a clientes) y restaurantes (tickets de
// Caja): se arma con bloques que cada empresa prende, apaga y ordena desde
// el botón de configuración (solo gerente o admin). Sin configurar, muestra
// los bloques de su rubro. El backend manda cada monto en BCV$ y USD.
// ============================================================

const NOMBRE_BLOQUE = {
  resumen: 'Resumen',
  por_mes: 'Ventas por mes',
  canal: 'Mostrador y delivery',
  productos_mas: 'Productos más vendidos',
  productos_menos: 'Productos menos vendidos',
  grupos: 'Ventas por grupo',
  clientes: 'Mejores clientes',
  cobranza: 'Cuentas por cobrar'
};
const ANCHO_COMPLETO = new Set(['resumen', 'por_mes']);
const PERIODOS = [
  { clave: 'mes', nombre: 'Este mes' },
  { clave: '3m', nombre: 'Últimos 3 meses' },
  { clave: '12m', nombre: 'Últimos 12 meses' }
];
const COLOR_FACTURAS = '#2563eb';
const COLOR_CAJA = '#74D5AD';
const TOP = 10;

// Eje Y compacto, como el gráfico del Dashboard.
function formatEje(v) {
  const abs = Math.abs(v);
  const signo = v < 0 ? '-' : '';
  return abs >= 1000 ? `${signo}${Math.round(abs / 1000)}K` : `${signo}${Math.round(abs)}`;
}

// "2026-08" -> "ago 26"
function mesCorto(mes) {
  const [a, m] = mes.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('es', { month: 'short', year: '2-digit' }).replace('.', '');
}

function Tarjeta({ titulo, children, accion }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-700">{titulo}</h2>
        {accion}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

const Vacio = ({ texto = 'Sin ventas en el período' }) => <p className="text-sm text-slate-400 py-6 text-center">{texto}</p>;

// Fila con barra proporcional (canales, grupos, tramos de cobranza).
function FilaBarra({ nombre, valor, maximo, vista, detalle, color = 'bg-primary-500' }) {
  const pct = maximo > 0 ? Math.max(0, (valor / maximo) * 100) : 0;
  return (
    <div className="py-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="text-slate-700 truncate">{nombre}</span>
        <span className="whitespace-nowrap font-medium text-slate-800">
          {pref(vista)}{formatMonto(valor)}
          {detalle && <span className="text-xs font-normal text-slate-400"> · {detalle}</span>}
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full mt-1">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function BloqueResumen({ d, k, vista }) {
  const r = d.resumen;
  const actual = d.por_mes[d.por_mes.length - 1];
  const anterior = d.por_mes[d.por_mes.length - 2];
  const mesK = (x) => (x ? x.facturas[k] + x.caja[k] : 0);
  const variacion = mesK(anterior) ? ((mesK(actual) - mesK(anterior)) / Math.abs(mesK(anterior))) * 100 : null;
  const kpi = (titulo, valor, sub) => (
    <div className="min-w-0">
      <p className="text-xs text-slate-500">{titulo}</p>
      <p className="text-xl font-bold text-slate-800 whitespace-nowrap">{valor}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
      {kpi('Ventas del período', `${pref(vista)}${formatMonto(r.total[k])}`,
        [r.facturas.documentos ? `${r.facturas.documentos} factura${r.facturas.documentos === 1 ? '' : 's'}` : null,
          r.caja.documentos ? `${r.caja.documentos} ticket${r.caja.documentos === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ') || 'Sin documentos')}
      {kpi(r.caja.documentos && !r.facturas.documentos ? 'Ticket promedio' : 'Venta promedio', `${pref(vista)}${formatMonto(r.promedio[k])}`, 'por factura o ticket')}
      {kpi('Este mes', `${pref(vista)}${formatMonto(mesK(actual))}`, 'hasta hoy')}
      {kpi('Mes anterior', `${pref(vista)}${formatMonto(mesK(anterior))}`,
        variacion == null ? null : (
          <span className={variacion >= 0 ? 'text-green-700' : 'text-red-600'}>
            Este mes va {variacion >= 0 ? '+' : ''}{formatMonto(variacion, 1)}%
          </span>
        ))}
    </div>
  );
}

function TooltipMes({ active, payload, label, vista, conCaja, conFacturas }) {
  if (!active || !payload?.length) return null;
  const v = (key) => payload.find((p) => p.dataKey === key)?.value ?? 0;
  return (
    <div className="text-xs shadow-lg rounded-lg px-3 py-2" style={{ background: '#131d26', color: '#fff' }}>
      <p className="text-slate-300 mb-1 capitalize">{label}</p>
      {conFacturas && <p>Facturas: {pref(vista)}{formatMonto(v('facturas'))}</p>}
      {conCaja && <p>Caja: {pref(vista)}{formatMonto(v('caja'))}</p>}
      {conCaja && conFacturas && <p className="font-medium">Total: {pref(vista)}{formatMonto(v('facturas') + v('caja'))}</p>}
    </div>
  );
}

function BloquePorMes({ d, k, vista }) {
  const datos = d.por_mes.map((m) => ({ mes: mesCorto(m.mes), facturas: m.facturas[k], caja: m.caja[k] }));
  const conCaja = datos.some((x) => x.caja);
  const conFacturas = datos.some((x) => x.facturas) || !conCaja;
  return (
    <Tarjeta
      titulo="Ventas por mes · últimos 12 meses"
      accion={conCaja && conFacturas && (
        <span className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLOR_FACTURAS }} />Facturas</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLOR_CAJA }} />Caja</span>
        </span>
      )}
    >
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={datos} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke="rgba(15,23,42,0.06)" />
          <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#89949e' }} axisLine={{ stroke: 'rgba(15,23,42,0.08)' }} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#89949e' }} axisLine={false} tickLine={false} width={48} tickFormatter={formatEje} />
          <Tooltip content={<TooltipMes vista={vista} conCaja={conCaja} conFacturas={conFacturas} />} cursor={{ fill: 'rgba(15,23,42,0.04)' }} />
          {conFacturas && <Bar dataKey="facturas" stackId="v" fill={COLOR_FACTURAS} radius={conCaja ? [0, 0, 0, 0] : [3, 3, 0, 0]} barSize={18} />}
          {conCaja && <Bar dataKey="caja" stackId="v" fill={COLOR_CAJA} radius={[3, 3, 0, 0]} barSize={18} />}
        </BarChart>
      </ResponsiveContainer>
    </Tarjeta>
  );
}

function BloqueCanal({ d, k, vista }) {
  const { canales, plataformas } = d.canal;
  const total = canales.reduce((s, c) => s + c[k], 0);
  return (
    <Tarjeta titulo="Mostrador y delivery">
      {canales.length === 0 ? <Vacio /> : (
        <>
          {canales.map((c) => (
            <FilaBarra key={c.clave} nombre={c.nombre} valor={c[k]} maximo={total} vista={vista}
              detalle={`${total ? formatMonto((c[k] / total) * 100, 0) : 0}% · ${c.documentos} doc.`} />
          ))}
          {plataformas.length > 0 && (
            <div className="mt-3 pt-2 border-t">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Delivery por plataforma</p>
              {plataformas.map((p) => (
                <FilaBarra key={p.nombre} nombre={p.nombre} valor={p[k]} maximo={plataformas[0][k]} vista={vista}
                  detalle={`${p.documentos} ticket${p.documentos === 1 ? '' : 's'}`} color="bg-emerald-400" />
              ))}
            </div>
          )}
        </>
      )}
    </Tarjeta>
  );
}

function TablaProductos({ titulo, filas, k, vista }) {
  return (
    <Tarjeta titulo={titulo}>
      {filas.length === 0 ? <Vacio /> : (
        <table className="w-full text-sm">
          <thead className="text-slate-500 text-left text-xs">
            <tr>
              <th className="py-1 pr-2 font-medium">Producto</th>
              <th className="py-1 px-2 font-medium text-right">Cantidad</th>
              <th className="py-1 pl-2 font-medium text-right">Monto</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="py-1.5 pr-2 text-slate-700">{p.nombre}</td>
                <td className="py-1.5 px-2 text-right whitespace-nowrap text-slate-500">{formatMonto(p.cantidad, 3)} {p.unidad === 'Unidad' ? 'und' : p.unidad}</td>
                <td className="py-1.5 pl-2 text-right whitespace-nowrap">{pref(vista)}{formatMonto(p[k])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Tarjeta>
  );
}

function BloqueGrupos({ d, k, vista }) {
  const grupos = [...d.grupos].sort((a, b) => b[k] - a[k]);
  const total = grupos.reduce((s, g) => s + g[k], 0);
  return (
    <Tarjeta titulo="Ventas por grupo">
      {grupos.length === 0 ? <Vacio /> : grupos.map((g) => (
        <FilaBarra key={g.id} nombre={g.nombre} valor={g[k]} maximo={grupos[0][k]} vista={vista}
          detalle={`${total ? formatMonto((g[k] / total) * 100, 0) : 0}%`} />
      ))}
    </Tarjeta>
  );
}

function BloqueClientes({ d, k, vista }) {
  const clientes = [...d.clientes].sort((a, b) => b[k] - a[k]).slice(0, TOP);
  return (
    <Tarjeta titulo="Mejores clientes">
      {clientes.length === 0 ? <Vacio texto="Sin ventas a clientes en el período" /> : clientes.map((c) => (
        <FilaBarra key={c.id} nombre={c.nombre} valor={c[k]} maximo={clientes[0][k]} vista={vista}
          detalle={`${c.documentos} doc.`} />
      ))}
    </Tarjeta>
  );
}

function BloqueCobranza({ d, k, vista }) {
  const navigate = useNavigate();
  const { total, tramos, documentos } = d.cobranza;
  const maximo = Math.max(...tramos.map((t) => t[k]), 0);
  return (
    <Tarjeta
      titulo="Cuentas por cobrar"
      accion={(
        <button type="button" onClick={() => navigate('/reportes/antiguedad-por-cobrar')} className="text-xs text-primary-600 hover:underline flex items-center">
          Ver detalle <ChevronRight size={14} />
        </button>
      )}
    >
      {documentos === 0 ? <Vacio texto="No hay facturas por cobrar" /> : (
        <>
          <p className="text-xl font-bold text-slate-800">{pref(vista)}{formatMonto(total[k])}</p>
          <p className="text-xs text-slate-400 mb-2">{documentos} factura{documentos === 1 ? '' : 's'} abierta{documentos === 1 ? '' : 's'} hoy</p>
          {tramos.map((t) => (
            <FilaBarra key={t.clave} nombre={t.nombre} valor={t[k]} maximo={maximo} vista={vista}
              detalle={`${t.documentos}`} color={t.clave === 'por_vencer' ? 'bg-green-500' : t.clave === 'd30' ? 'bg-amber-400' : 'bg-red-500'} />
          ))}
        </>
      )}
    </Tarjeta>
  );
}

function ConfigBloques({ datos, onCerrar, onGuardado }) {
  const [lista, setLista] = useState(() => [
    ...datos.bloques.map((b) => ({ clave: b, activo: true })),
    ...datos.bloques_disponibles.filter((b) => !datos.bloques.includes(b)).map((b) => ({ clave: b, activo: false }))
  ]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  function mover(i, delta) {
    const j = i + delta;
    if (j < 0 || j >= lista.length) return;
    const copia = [...lista];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    setLista(copia);
  }

  async function guardar(bloques) {
    setGuardando(true);
    setError('');
    try {
      const { data } = await api.put('/reportes/ventas-dashboard/bloques', { bloques });
      onGuardado(data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar');
      setGuardando(false);
    }
  }

  return (
    <Modal title="Bloques del dashboard" onClose={onCerrar}>
      <p className="text-xs text-slate-500 mb-3">Elige qué ve esta empresa y en qué orden. Aplica a todos los usuarios de la empresa.</p>
      <ul className="divide-y border rounded-lg">
        {lista.map((b, i) => (
          <li key={b.clave} className="flex items-center gap-2 px-3 py-2">
            <input
              type="checkbox"
              checked={b.activo}
              onChange={() => setLista(lista.map((x) => (x.clave === b.clave ? { ...x, activo: !x.activo } : x)))}
              className="h-4 w-4"
              id={`bloque-${b.clave}`}
            />
            <label htmlFor={`bloque-${b.clave}`} className={`flex-1 text-sm ${b.activo ? 'text-slate-800' : 'text-slate-400'}`}>
              {NOMBRE_BLOQUE[b.clave]}
            </label>
            <button type="button" onClick={() => mover(i, -1)} disabled={i === 0} title="Subir" className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30">
              <ChevronUp size={16} />
            </button>
            <button type="button" onClick={() => mover(i, 1)} disabled={i === lista.length - 1} title="Bajar" className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30">
              <ChevronDown size={16} />
            </button>
          </li>
        ))}
      </ul>
      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      <div className="flex items-center justify-between gap-2 mt-4">
        <button type="button" disabled={guardando} onClick={() => guardar(null)} className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50">
          Volver a los de {datos.rubro === 'restaurante' ? 'restaurante' : 'fábrica'}
        </button>
        <button
          type="button"
          disabled={guardando || !lista.some((b) => b.activo)}
          onClick={() => guardar(lista.filter((b) => b.activo).map((b) => b.clave))}
          className="bg-primary-600 text-white rounded-md py-2 px-5 text-sm hover:bg-primary-700 disabled:opacity-50"
        >
          Guardar
        </button>
      </div>
    </Modal>
  );
}

export default function Ventas() {
  const { empresaActiva, empresaActivaNombre, rolActual, esAdminGlobal } = useAuth();
  const [periodo, setPeriodo] = useState('12m');
  const [vista, setVista] = useVistaMoneda();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [configurando, setConfigurando] = useState(false);
  const puedeConfigurar = esAdminGlobal || ['gerente', 'admin'].includes(rolActual);

  useEffect(() => {
    if (!empresaActiva) return;
    let vigente = true;
    setCargando(true);
    setError('');
    api.get('/reportes/ventas-dashboard', { params: { periodo } })
      .then(({ data }) => { if (vigente) setDatos(data); })
      .catch((err) => { if (vigente) setError(err.response?.data?.error || 'No se pudo cargar el dashboard de ventas'); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [empresaActiva, periodo]);

  const k = vista === 'BCV' ? 'bcv' : 'usd';

  // Excel: una hoja por bloque visible del dashboard, en la moneda elegida.
  function contenidoExcel() {
    const m = { titulo: `Monto ${vista}`, tipo: 'monto' };
    const porMonto = (lista) => [...lista].sort((a, b) => b[k] - a[k]);
    const hojas = [];
    const vistos = new Set();
    for (const b of datos.bloques) {
      const clave = b.startsWith('productos') ? 'productos' : b;
      if (vistos.has(clave)) continue;
      vistos.add(clave);
      if (clave === 'resumen') {
        const r = datos.resumen;
        hojas.push({
          nombre: 'Resumen',
          columnas: [{ titulo: 'Concepto' }, { titulo: 'Documentos', tipo: 'entero' }, m],
          filas: [['Facturas (menos notas de crédito)', r.facturas.documentos, r.facturas[k]], ['Tickets de caja', r.caja.documentos, r.caja[k]]],
          total: ['Total', r.facturas.documentos + r.caja.documentos, r.total[k]]
        });
      } else if (clave === 'por_mes') {
        hojas.push({
          nombre: 'Por mes',
          columnas: [{ titulo: 'Mes' }, { titulo: `Facturas ${vista}`, tipo: 'monto' }, { titulo: `Caja ${vista}`, tipo: 'monto' }, { titulo: `Total ${vista}`, tipo: 'monto' }],
          filas: datos.por_mes.map((x) => [x.mes, x.facturas[k], x.caja[k], x.facturas[k] + x.caja[k]])
        });
      } else if (clave === 'canal') {
        hojas.push({
          nombre: 'Por canal',
          columnas: [{ titulo: 'Canal o plataforma' }, { titulo: 'Documentos', tipo: 'entero' }, m],
          filas: [
            ...datos.canal.canales.map((c) => [c.nombre, c.documentos, c[k]]),
            ...datos.canal.plataformas.map((x) => [`   ${x.nombre} (delivery)`, x.documentos, x[k]])
          ]
        });
      } else if (clave === 'productos') {
        hojas.push({
          nombre: 'Productos',
          columnas: [{ titulo: 'Producto' }, { titulo: 'Cantidad', tipo: 'monto' }, { titulo: 'Unidad' }, m],
          filas: porMonto(datos.productos).map((x) => [x.nombre, x.cantidad, x.unidad, x[k]])
        });
      } else if (clave === 'grupos') {
        hojas.push({ nombre: 'Por grupo', columnas: [{ titulo: 'Grupo' }, m], filas: porMonto(datos.grupos).map((x) => [x.nombre, x[k]]) });
      } else if (clave === 'clientes') {
        hojas.push({
          nombre: 'Clientes',
          columnas: [{ titulo: 'Cliente' }, { titulo: 'Documentos', tipo: 'entero' }, m],
          filas: porMonto(datos.clientes).map((x) => [x.nombre, x.documentos, x[k]])
        });
      } else if (clave === 'cobranza') {
        hojas.push({
          nombre: 'Por cobrar',
          columnas: [{ titulo: 'Antigüedad' }, { titulo: 'Documentos', tipo: 'entero' }, m],
          filas: datos.cobranza.tramos.map((t) => [t.nombre, t.documentos, t[k]]),
          total: ['Total', datos.cobranza.documentos, datos.cobranza.total[k]]
        });
      }
    }
    return {
      titulo: 'Ventas',
      detalle: `Del ${formatFecha(datos.desde)} al ${formatFecha(datos.hasta)} · montos en ${vista}, sin IVA`,
      hojas: hojas.length ? hojas : [{ columnas: [{ titulo: 'Sin bloques' }], filas: [] }]
    };
  }

  function bloque(clave) {
    const props = { d: datos, k, vista };
    const productosOrden = [...datos.productos].sort((a, b) => b[k] - a[k]);
    switch (clave) {
      case 'resumen': return <BloqueResumen {...props} />;
      case 'por_mes': return <BloquePorMes {...props} />;
      case 'canal': return <BloqueCanal {...props} />;
      case 'productos_mas': return <TablaProductos titulo="Productos más vendidos" filas={productosOrden.slice(0, TOP)} k={k} vista={vista} />;
      case 'productos_menos': return <TablaProductos titulo="Productos menos vendidos" filas={productosOrden.slice(-TOP).reverse()} k={k} vista={vista} />;
      case 'grupos': return <BloqueGrupos {...props} />;
      case 'clientes': return <BloqueClientes {...props} />;
      case 'cobranza': return <BloqueCobranza {...props} />;
      default: return null;
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Ventas
          {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="border rounded-md px-3 py-2 text-sm">
            {PERIODOS.map((p) => <option key={p.clave} value={p.clave}>{p.nombre}</option>)}
          </select>
          <SelectorMoneda vista={vista} onChange={setVista} />
          {puedeConfigurar && datos && (
            <button
              type="button"
              onClick={() => setConfigurando(true)}
              title="Elegir bloques del dashboard"
              className="p-2 rounded-md text-white bg-slate-600 hover:bg-slate-700 transition-colors"
            >
              <SlidersHorizontal size={18} />
            </button>
          )}
          <Exportar excel={contenidoExcel} disabled={!datos} />
        </div>
      </div>

      {datos && (
        <EncabezadoImpresion
          titulo="Ventas"
          detalle={`Del ${formatFecha(datos.desde)} al ${formatFecha(datos.hasta)} · montos en ${vista}, sin IVA`}
        />
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {cargando && !datos ? (
        <p className="text-slate-400 text-sm">Cargando…</p>
      ) : datos && (
        <>
          <p className="text-xs text-slate-500">
            Del {formatFecha(datos.desde)} al {formatFecha(datos.hasta)} (el gráfico mensual siempre muestra 12 meses). Montos sin IVA: facturas menos notas de crédito, más tickets cobrados de Caja.
          </p>
          {datos.sin_tasa > 0 && (
            <p className="flex items-center gap-1.5 text-sm text-amber-700">
              <AlertTriangle size={15} />
              {datos.sin_tasa} documento(s) sin tasa de cambio no se pudieron convertir y no suman.
            </p>
          )}
          {datos.bloques.length === 0 ? (
            <p className="text-sm text-slate-400">No hay bloques elegidos para esta empresa.</p>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${cargando ? 'opacity-60' : ''}`}>
              {datos.bloques.map((b) => (
                <div key={b} className={ANCHO_COMPLETO.has(b) ? 'md:col-span-2' : ''}>{bloque(b)}</div>
              ))}
            </div>
          )}
        </>
      )}

      {configurando && (
        <ConfigBloques
          datos={datos}
          onCerrar={() => setConfigurando(false)}
          onGuardado={(cfg) => { setDatos({ ...datos, ...cfg }); setConfigurando(false); }}
        />
      )}
    </div>
  );
}
